import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService, type LoginCredentials } from '@/services/auth.service'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<any>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isSuperAdmin = computed(() => user.value?.role === 'SUPER_ADMIN')
  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const isManager = computed(() => user.value?.role === 'MANAGER')
  const isEmployee = computed(() => user.value?.role === 'EMPLOYEE')
  const isViewer = computed(() => user.value?.role === 'VIEWER')
  const isInternalUser = computed(() => user.value?.userType === 'INTERNAL')
  const isExternalUser = computed(() => user.value?.userType === 'EXTERNAL')

  // Actions
  const login = async (credentials: LoginCredentials) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await authService.login(credentials)
      user.value = response.user
      token.value = response.access_token
      
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Erreur de connexion'
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      loading.value = true
      await authService.logout()
    } catch (err: any) {
      console.warn('Erreur lors de la déconnexion:', err)
    } finally {
      // Nettoyer l'état même en cas d'erreur
      user.value = null
      token.value = null
      error.value = null
      loading.value = false
    }
  }

  const checkAuth = () => {
    const storedToken = authService.getToken()
    const storedUser = authService.getCurrentUser()
    
    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = storedUser
    }
  }

  const updateUser = (userData: any) => {
    user.value = userData
  }

  const clearError = () => {
    error.value = null
  }

  const hasPermission = (permission: string): boolean => {
    // Utiliser l'utilisateur du store plutôt que localStorage pour être sûr d'avoir les données à jour
    if (!user.value || !user.value.permissions) {
      return false
    }
    return user.value.permissions.includes(permission)
  }

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role)
  }

  const isUserType = (userType: string): boolean => {
    return authService.isUserType(userType)
  }

  // Permissions spécifiques aux dashboards
  const canViewDashboard = computed(() => hasPermission('dashboard:view'))
  const canCreateDashboard = computed(() => hasPermission('dashboard:create'))
  const canUpdateDashboard = computed(() => hasPermission('dashboard:update'))
  const canDeleteDashboard = computed(() => hasPermission('dashboard:delete'))
  const canPublishDashboard = computed(() => hasPermission('dashboard:publish'))
  const canConfigureDashboard = computed(() => hasPermission('dashboard:configure'))
  const canCustomizeDashboard = computed(() => hasPermission('dashboard:customize'))

  const refreshToken = async () => {
    try {
      const newToken = await authService.refreshToken()
      token.value = newToken
      return newToken
    } catch (err: any) {
      // Si le refresh échoue, déconnecter l'utilisateur
      await logout()
      throw err
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      loading.value = true
      error.value = null
      await authService.changePassword(currentPassword, newPassword)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Erreur lors du changement de mot de passe'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (profileData: any) => {
    try {
      loading.value = true
      error.value = null
      const updatedUser = await authService.updateProfile(profileData)
      user.value = updatedUser
      return updatedUser
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Erreur lors de la mise à jour du profil'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    
    // Getters
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    isManager,
    isEmployee,
    isViewer,
    isInternalUser,
    isExternalUser,
    
    // Actions
    login,
    logout,
    checkAuth,
    updateUser,
    clearError,
    hasPermission,
    hasRole,
    isUserType,
    refreshToken,
    changePassword,
    
    // Permissions dashboard
    canViewDashboard,
    canCreateDashboard,
    canUpdateDashboard,
    canDeleteDashboard,
    canPublishDashboard,
    canConfigureDashboard,
    canCustomizeDashboard,
    updateProfile
  }
})
